//  type for an individual option
export type ExportOptionItem = {
  id:
    | 'eventReport'
    | 'classReport'
    | 'scoreReport'
    | 'scheduleReport'
    | 'stallReport'
    | 'feesReport'
    | 'personnelReport'
    | 'teamReport'
    | 'questionsReport'
    | 'horseReport'
    | 'riderReport'
    | 'trainerReport'
    | 'ownerReport'
    | 'guardianReport'
    | 'judgeReport'
    | 'staffReport'
    | 'sponsorReport'
    | 'onsiteVendorReport'
    | 'virtualVendorReport'
  iconSrc: string
  iconAlt: string
  title: string
  description: string
}

//  type for the enabled options state object
export type EnabledOptionsType = {
  [key in ExportOptionItem['id']]: boolean
}

export const CUSTOM_DATA_EXPORT_OPTIONS = [
  {
    id: 'eventReport',
    iconSrc: 'assets/og_icons/award-icon-with-background.svg',
    iconAlt: 'Icon for the event report export',
    title: 'Event Report',
    description:
      'A high level summary of the total numbers for this event. Eg. Number of entries, staff, ticket sales, sponsors',
  },
  {
    id: 'classReport',
    iconSrc: 'assets/og_icons/list-icon-with-background.svg',
    iconAlt: 'Icon for the class report export',
    title: 'Class Report',
    description:
      'Event data broken down by class including number of entries, scores, prize money, etc',
  },
  {
    id: 'scoreReport',
    iconSrc: 'assets/og_icons/award-icon-with-background.svg',
    iconAlt: 'Icon for the score report export',
    title: 'Score Report',
    description: 'Score data grouped by class with rider, horse and prize money details',
  },
  {
    id: 'scheduleReport',
    iconSrc: 'assets/og_icons/clock-icon-with-background.svg',
    iconAlt: 'Icon for the schedule report export',
    title: 'Schedule Report',
    description: 'Order of go data broken down by class, facility and rider',
  },
  {
    id: 'stallReport',
    iconSrc: 'assets/og_icons/stall-icon-with-background.svg',
    iconAlt: 'Icon for the stall report export',
    title: 'Stall Report',
    description: 'Stall data broken down by rider, trainer, horse with feed and bedding supplies',
  },
  {
    id: 'feesReport',
    iconSrc: 'assets/og_icons/money-icon-with-background.svg',
    iconAlt: 'Icon for the fees report export',
    title: 'Fees Report',
    description:
      'Detailed breakdown of every fee associated with an entry and their payment status',
  },
  {
    id: 'personnelReport',
    iconSrc: 'assets/og_icons/people-icon-with-background.svg',
    iconAlt: 'Icon for the personnel report export',
    title: 'Personnel Report',
    description: 'Number of exhibitors, staff and spectators broken down per category',
  },
  {
    id: 'teamReport',
    iconSrc: 'assets/og_icons/people-icon-with-background.svg',
    iconAlt: 'Icon for the team report export',
    title: 'Team Report',
    description:
      'High level overview of the different teams attending the event, defined by the Registrar',
  },
  {
    id: 'questionsReport',
    iconSrc: 'assets/og_icons/question-icon-with-background.svg',
    iconAlt: 'Icon for the questions report export',
    title: 'Questions Report',
    description: 'Answers to the custom questions created by event organizer, grouped by team',
  },
  {
    id: 'horseReport',
    iconSrc: 'assets/og_icons/horse-icon-with-background.svg',
    iconAlt: 'Icon for the horse report export',
    title: 'Horse Report',
    description:
      'Detailed breakdown of every horse in attendance, including their biographical information',
  },
  {
    id: 'riderReport',
    iconSrc: 'assets/og_icons/person-icon-with-background.svg',
    iconAlt: 'Icon for the rider report export',
    title: 'Rider Report',
    description:
      'Detailed breakdown of every rider in attendance, including their biographical information',
  },
  {
    id: 'trainerReport',
    iconSrc: 'assets/og_icons/person-icon-with-background.svg',
    iconAlt: 'Icon for the trainer report export',
    title: 'Trainer Report',
    description:
      'Detailed breakdown of every trainer in attendance, including their biographical information',
  },
  {
    id: 'ownerReport',
    iconSrc: 'assets/og_icons/person-icon-with-background.svg',
    iconAlt: 'Icon for the owner report export',
    title: 'Owner Report',
    description:
      'Detailed breakdown of every owner in attendance, including their biographical information',
  },
  {
    id: 'guardianReport',
    iconSrc: 'assets/og_icons/shield-icon-with-background.svg',
    iconAlt: 'Icon for the gaurdian report export',
    title: 'Gaurdian Report',
    description:
      'Detailed breakdown of every guardian in attendance, including their biographical information',
  },
  {
    id: 'judgeReport',
    iconSrc: 'assets/og_icons/judge-icon-with-background.svg',
    iconAlt: 'Icon for the judge report export',
    title: 'Judge Report',
    description:
      'Detailed breakdown of every judge in attendance, including their biographical information and scores',
  },
  {
    id: 'staffReport',
    iconSrc: 'assets/og_icons/person-clipboard-icon-with-background.svg',
    iconAlt: 'Icon for the staff report export',
    title: 'Staff Report',
    description: 'Detailed list of every staff member in attendance with contact information',
  },
  {
    id: 'sponsorReport',
    iconSrc: 'assets/og_icons/medal-icon-with-background.svg',
    iconAlt: 'Icon for the sponsor report export',
    title: 'Sponsor Report',
    description: 'Detailed list of every sponsor with items purchased and contact information',
  },
  {
    id: 'onsiteVendorReport',
    iconSrc: 'assets/og_icons/vendor-icon-with-background.svg',
    iconAlt: 'Icon for the onsite vendor report export',
    title: 'Onsite Vendor Report',
    description:
      'Detailed list of every on-site vendor with items purchased and contact information',
  },
  {
    id: 'virtualVendorReport',
    iconSrc: 'assets/og_icons/vendor-icon-with-background.svg',
    iconAlt: 'Icon for the virtual vendor report export',
    title: 'Virtual Vendor Report',
    description:
      'Detailed list of every virtual vendor with items purchased and contact information',
  },
]

// Event report fields (vertical)
export const EVENT_REPORT_FIELDS = [
  'Competition Date',
  'Governing Body Comp ID Number',
  'Number Of Entries',
  'Number Of Scratches',
  'Number That Competed',
  'Number That Completed',
  'Run Status',
  'Prize Money Awarded',
  'Prize Money Offered',
  'Number Of Riders',
  'Number Of Trainers',
  'Number Of Owners',
  'Number Of Guardians',
  'Number Of Show Manager',
  'Number Of Secretary',
  'Number Of Course Designer',
  'Number Of Technical Delegate',
  'Number Of Judges',
  'Number Of Scorers',
  'Number Of Stewards',
  'Number Of Ingates',
  'Number Of Announcers',
  'Number Of Farriers',
  'Number Of Veterinarians',
  'Number Of Press',
  'Number Of Other Staff',
  'Count Of All Staff Roles',
  'Number Of Spectator Tickets',
  'Number Of Press Passes',
  'Number Of Sponsors',
  'Number Of On Site Vendors',
  'Number Of Virtual Vendors',
]

// Type to map each field name to value type for vertical report
export type EventReportData = {
  [key in (typeof EVENT_REPORT_FIELDS)[number]]: string | number | null
}

// Class report columns
export const CLASS_REPORT_COLUMNS = [
  'Class Number',
  'Class Title',
  'Number Of Entries',
  'Number That Competed',
  'Number That Completed',
  'Number Of Bonus Fee Collected',
  'Number Of Bonus Fee Sold',
  'Prize Money Awarded',
  'Prize Money Offered',
]

export type ClassReportRow = {
  [key in (typeof CLASS_REPORT_COLUMNS)[number]]: string | number
}

// Score report columns
export const SCORE_REPORT_COLUMNS = [
  'Class Number',
  'Class Title',
  'Rider Name',
  'Horse Name',
  'Back Number',
  'Score (Total)',
  'Percentage (Total)',
  'Score Level',
  'Placing',
  'Number Of Entries',
  'Number That Competed',
  'Number That Completed',
  'Prize Money Offered',
  'Prize Money Awarded',
  'Ride Status',
]

export type ScoreReportRow = {
  [key in (typeof CLASS_REPORT_COLUMNS)[number]]: string | number
}

export const SCHEDULE_REPORT_COLUMNS = [
  'Class Date',
  'Facility',
  'Class Number',
  'Class Title',
  'Order Of Go',
  'Ride Time',
  'Rider Name',
  'Horse Name',
  'Back Number',
  'Ride Status',
]

export type ScheduleReportRow = {
  [key in (typeof SCHEDULE_REPORT_COLUMNS)[number]]: string | number
}

// Stall report columns
export const STALL_REPORT_COLUMNS = [
  'Rider Name',
  'Horses',
  'Stall',
  'Bedding',
  'Feed',
  'Total Items',
]

export type StallReportRow = {
  [key in (typeof STALL_REPORT_COLUMNS)[number]]: string | number
}

// Fees report columns (vertical)
export const FEES_REPORT_COLUMNS = ['Fee', 'Total Sold']

export type FeesReportRow = {
  [key in (typeof FEES_REPORT_COLUMNS)[number]]: string | number
}

// Personnel report fields (vertical)
export const PERSONNEL_REPORT_FIELDS = [
  'Number Of Riders',
  'Number Of Trainers',
  'Number Of Owners',
  'Number Of Guardians',
  'Number Of Show Managers',
  'Number Of Secretary',
  'Number Of Course Designer',
  'Number Of Technical Delegate',
  'Number Of Judges',
  'Number Of Scorers',
  'Number Of Stewards',
  'Number Of Ingates',
  'Number Of Announcers',
  'Number Of Farriers',
  'Number Of Veterinarians',
  'Number Of Press',
  'Number Of Other Staff',
  'Count Of All Staff Roles',
]

// Type to map each field name to value type for vertical report
export type PersonnelReportData = {
  [key in (typeof PERSONNEL_REPORT_FIELDS)[number]]: string | number | null
}

// Team report columns
export const TEAM_REPORT_COLUMNS = [
  'Role',
  'Name',
  'Phone',
  'Email',
  'Paperwork Status',
  'Rider',
  'Horse',
]

export type TeamReportRow = {
  [key in (typeof TEAM_REPORT_COLUMNS)[number]]: string | number
}

// Custom questions report columns
export const CUSTOM_QUESTIONS_REPORT_COLUMNS = [
  'Registrar Name',
  'Team Members',
  'Team Names',
  'Entry Status',
  'Horse Names',
  'Question',
  'Answer',
  'Email',
  'Phone',
  'Message User (Click to open)',
]

export type CustomQuestionsRow = {
  [key in (typeof CUSTOM_QUESTIONS_REPORT_COLUMNS)[number]]: string | number
}
// Horse report columns
export const HORSE_REPORT_COLUMNS = [
  'Horse Name',
  'Horse Breed',
  'Horse Breeder',
  'Horse Height',
  'Horse Year Of Birth',
  'Horse Gender',
  'Horse Gelding Status',
  'Horse Discipline',
  'Horse Zone',
  'Horse Coloring',
  'Horse Markings',
  'Horse Country Of Origin',
  'Horse Microchip Number',
  'Horse DNA Case Number',
  'Horse Passport Number',
  'Coggins Certificate',
  'Health Certificate',
  'Vaccination Certificate',
  'Rhinopneumonitis Certificate',
  'Influenza Certificate',
  'Horse Sire',
  'Horse Dam',
  'Horse Grand Sire',
  'Horse Grand Dam',
  'Horses Membership Numbers (Name, Number)',
]

export type HorseReportRow = {
  [key in (typeof HORSE_REPORT_COLUMNS)[number]]: string | number
}

// Rider report columns
export const RIDER_REPORT_COLUMNS = [
  'Rider First Name',
  'Rider Last Name',
  'Rider Nationality',
  'Back Number',
  'If No Member Number, Why?',
  'Riders Trainer Full Name',
  'Riders Owner Full Name',
  'Riders Coach Full Name',
  'Riders Guardian Full Name',
  'Rider Discipline',
  'Rider Zone',
  'Rider Amateur Pro Status',
  'Rider Judging License',
  'Rider Foreign Endorsement Letter (Yes/No, Date Valid)',
  'Rider Email',
  'Rider Phone Number',
  'Rider Street Address',
  'Rider City',
  'Rider State',
  'Rider Zip',
  'Rider Membership Numbers',
]

export type RiderReportRow = {
  [key in (typeof RIDER_REPORT_COLUMNS)[number]]: string | number
}

// Owner report columns
export const OWNER_REPORT_COLUMNS = [
  'Owner Name',
  'Horse Name',
  'Rider Name',
  'Owner Email',
  'Owner Phone Number',
  'Owner Street Address',
  'Owner City',
  'Owner State',
  'Owner Zip',
  'Owner Nationality',
  'Owners Membership Numbers',
]

export type OwnerReportRow = {
  [key in (typeof OWNER_REPORT_COLUMNS)[number]]: string | number
}

// Trainer report columns
export const TRAINER_REPORT_COLUMNS = [
  'Trainer Name',
  'Horse Name',
  'Rider Name',
  'Trainer Email',
  'Trainer Phone Number',
  'Trainer Address',
  'Trainer City',
  'Trainer State',
  'Trainer Zip',
  'Trainer Nationality',
  'Trainers Membership Numbers',
]

export type TrainerReportRow = {
  [key in (typeof TRAINER_REPORT_COLUMNS)[number]]: string | number
}

// Guardian report columns
export const GUARDIAN_REPORT_COLUMNS = [
  'Guardian Name',
  'Minor',
  'Guardian Email',
  'Guardian Phone Number',
  'Guardian Address',
  'Guardian City',
  'Guardian State',
  'Guardian Zip',
  'Guardian Nationality',
  'Guardians Membership Numbers',
  'Minor Paperwork Status',
]

export type GuardianReportRow = {
  [key in (typeof GUARDIAN_REPORT_COLUMNS)[number]]: string | number
}

// Judge report columns
export const JUDGE_REPORT_COLUMNS = [
  'Class Name',
  'Rider Name',
  'Horse Name',
  'Judge 1 First Name',
  'Judge 1 Last Name',
  'Judging License',
  'Judge 1 Percentage',
  'Judge 1 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 1 Email',
  'Judge 1 Phone Number',
  'Judge 1 Nationality',
  'Judge 1 Membership Numbers',
  'Judge 2 First Name',
  'Judge 2 Last Name',
  'Judging License',
  'Judge 2 Percentage',
  'Judge 2 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 2 Email',
  'Judge 2 Phone Number',
  'Judge 2 Nationality',
  'Judge 2 Membership Numbers',
  'Judge 3 First Name',
  'Judge 3 Last Name',
  'Judging License',
  'Judge 3 Percentage',
  'Judge 3 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 3 Email',
  'Judge 3 Phone Number',
  'Judge 3 Nationality',
  'Judge 3 Membership Numbers',
  'Judge 4 First Name',
  'Judge 4 Last Name',
  'Judging License',
  'Judge 4 Percentage',
  'Judge 4 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 4 Email',
  'Judge 4 Phone Number',
  'Judge 4 Nationality',
  'Judge 4 Membership Numbers',
  'Judge 5 First Name',
  'Judge 5 Last Name',
  'Judging License',
  'Judge 5 Percentage',
  'Judge 5 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 5 Email',
  'Judge 5 Phone Number',
  'Judge 5 Nationality',
  'Judge 5 Membership Numbers',
  'Judge 6 First Name',
  'Judge 6 Last Name',
  'Judging License',
  'Judge 6 Percentage',
  'Judge 6 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 6 Email',
  'Judge 6 Phone Number',
  'Judge 6 Nationality',
  'Judge 6 Membership Numbers',
  'Judge 7 First Name',
  'Judge 7 Last Name',
  'Judging License',
  'Judge 7 Percentage',
  'Judge 7 Score (Optional Except Dressage)',
  'Score Card Signed?',
  'Judge 7 Email',
  'Judge 7 Phone Number',
  'Judge 7 Nationality',
  'Judge 7 Membership Numbers',
]

export type JudgeReportRow = {
  [key: string]: string | number | null
}

// Staff report columns
export const STAFF_REPORT_COLUMNS = [
  'Staff Title',
  'Staff First Name',
  'Staff Last Name',
  'Staff Phone Number',
  'Staff Email',
  'Staff Membership Number',
]

export type StaffReportRow = {
  [key in (typeof STAFF_REPORT_COLUMNS)[number]]: string | number
}

// Sponsor report columns
export const SPONSOR_REPORT_COLUMNS = [
  'Sponsor Name',
  'Sponsor Item Purchased',
  'Sponsor Phone Number',
  'Sponsor Email',
]

export type SponsorReportRow = {
  [key in (typeof SPONSOR_REPORT_COLUMNS)[number]]: string | number
}

// Onsite vendor report columns
export const ONSITE_VENDOR_REPORT_COLUMNS = [
  'Onsite Vendor Name',
  'Onsite Vendor Item Purchased',
  'Onsite Vendor Phone Number',
  'Onsite Vendor Email',
]

export type OnsiteVendorReportRow = {
  [key in (typeof ONSITE_VENDOR_REPORT_COLUMNS)[number]]: string | number
}

// Virtual vendor report columns
export const VIRTUAL_VENDOR_REPORT_COLUMNS = [
  'Virtual Vendor Name',
  'Virtual Vendor Item Purchased',
  'Virtual Vendor Phone Number',
  'Virtual Vendor Email',
]

export type VirtualVendorReportRow = {
  [key in (typeof VIRTUAL_VENDOR_REPORT_COLUMNS)[number]]: string | number
}
